/**
 * API Service for Foresight Backend
 */

import { Alert, IncidentMetrics } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface AlertsResponse {
  data: Alert[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_items: number;
    per_page: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export interface AnalysisItem {
  id: string;
  alert_id: string;
  version: number;
  status: 'COMPLETED' | 'PENDING' | 'FAILED' | 'IN_PROGRESS';
  confidence_score: number | null;
  analysis_result: any;
  error_message: string | null;
  analysis_time_ms: number | null;
  created_at: string;
  updated_at: string;
}

export interface AnalysisResponse {
  data: AnalysisItem[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_items: number;
    per_page: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export interface RootCauseEvidence {
  type: 'code' | 'config' | 'metrics' | 'logs';
  title?: string;
  description?: string;
  source: string;
  source_url?: string;
  snippet?: string;
  data?: string;
  issue: string;
  impact?: string;
  trace_id?: string;
  daily_breakdown?: Array<{ date: string; count: string }>;
  trigger_scenarios?: string[];
}

export interface TimelineEvent {
  time: string;
  event: string;
  metric: string;
}

export interface RecommendedAction {
  priority: number;
  title: string;
  description: string;
  implementation?: {
    file: string;
    change: string;
  };
  impact: string;
  auto_fix: boolean;
}

export interface SimilarIncident {
  id: string;
  title: string;
  date: string;
  resolution: string;
  time_to_resolve: string;
}

export interface FiveWhysStep {
  why_number: number;
  question: string;
  answer: string;
  evidence: string;
  services_affected?: Array<{
    name: string;
    error_percentage: number;
    validation_status: string;
  }>;
  validation_gaps?: {
    no_validation: string[];
    late_validation: string[];
    permission_blocked: string[];
  };
  architecture_gaps?: string[];
  design_tradeoffs?: {
    pros: string[];
    cons: string[];
  };
  permission_barrier?: string;
  root_causes_ranked?: Array<{
    rank: number;
    severity: string;
    cause: string;
    impact: string;
    fix: string;
  }>;
}

export interface AlertAnalysis {
  alert_id: string;
  status: 'COMPLETED' | 'PENDING' | 'FAILED';
  analysis_time?: string;
  confidence?: number;
  message?: string;
  summary?: string;
  five_whys_analysis?: FiveWhysStep[];
  root_cause?: {
    title: string;
    description: string;
    summary?: string;
    evidence: RootCauseEvidence[];
    timeline: TimelineEvent[];
  };
  recommended_actions?: RecommendedAction[];
  similar_incidents?: SimilarIncident[];
  business_impact_analysis?: {
    revenue_at_risk: string;
    customers_affected: number;
    critical_path: string;
    estimated_resolution_time: string;
  };
  prevention?: {
    method: string;
    detection_time: string;
    value: string;
  };
}

export interface ServiceNode {
  id: string;
  position: { x: number; y: number };
  data: {
    label: string;
    status: 'healthy' | 'degraded' | 'critical' | 'stable';
    error_percentage?: number;
    validation_status?: string;
    description?: string;
  };
}

export interface ServiceEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
}

export interface ServiceDAG {
  alert_id: string;
  title?: string;
  nodes: ServiceNode[];
  edges: ServiceEdge[];
  metadata?: {
    total_services: number;
    critical_services: number;
    services_without_validation: number;
    primary_error_source: string;
    validation_point: string;
  };
  message?: string;
}

class ApiService {
  private async fetchWithAuth(endpoint: string, token: string, options?: RequestInit) {
    const url = `${API_BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `API Error: ${response.status}`);
    }

    return response.json();
  }

  async getAlerts(token: string, page: number = 1, perPage: number = 20): Promise<AlertsResponse> {
    return this.fetchWithAuth(`/v1/alerts?page=${page}&per_page=${perPage}`, token);
  }

  async getAlertAnalysis(alertId: string, token: string): Promise<AnalysisResponse> {
    return this.fetchWithAuth(`/v1/alerts/${alertId}/analysis`, token);
  }

  async triggerAnalysis(alertId: string, token: string): Promise<{id: string, alert_id: string, version: number}> {
    return this.fetchWithAuth(`/v1/alerts/${alertId}/analysis`, token, {
      method: 'POST',
    });
  }

  async getConfig(token: string): Promise<any> {
    return this.fetchWithAuth('/v1/configs', token);
  }

  async updateServicesConfig(token: string, config: any): Promise<any> {
    return this.fetchWithAuth('/v1/configs/services', token, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async createService(token: string, serviceName: string, config: any): Promise<any> {
    // Flatten the nested config structure to match API expectations
    const flattenedConfig = {
      service_name: serviceName,
      team_id: config.team_id,
      description: config.description,
      github_repo: config.github.repo,
      github_default_branch: config.github.default_branch,
      grafana_dashboard_uid: config.grafana.dashboard_uid,
      grafana_dashboard_name: config.grafana.dashboard_name,
      grafana_namespace: config.grafana.variables?.namespace || '',
      grafana_prometheus_datasource_uid: config.grafana.prometheus_datasource_uid || '',
      grafana_loki_datasource_uid: config.grafana.loki_datasource_uid || ''
    };

    return this.fetchWithAuth('/v1/configs/services', token, {
      method: 'POST',
      body: JSON.stringify(flattenedConfig),
    });
  }

  async updateService(token: string, serviceId: string, config: any): Promise<any> {
    // Flatten the nested config structure to match API expectations
    // Only include fields that are actually set (for partial updates)
    const flattenedConfig: any = {};

    if (config.team_id !== undefined && config.team_id !== '') flattenedConfig.team_id = config.team_id;
    if (config.description !== undefined && config.description !== '') flattenedConfig.description = config.description;
    if (config.github?.repo !== undefined && config.github.repo !== '') flattenedConfig.github_repo = config.github.repo;
    if (config.github?.default_branch !== undefined && config.github.default_branch !== '') flattenedConfig.github_default_branch = config.github.default_branch;
    if (config.grafana?.dashboard_uid !== undefined && config.grafana.dashboard_uid !== '') flattenedConfig.grafana_dashboard_uid = config.grafana.dashboard_uid;
    if (config.grafana?.dashboard_name !== undefined && config.grafana.dashboard_name !== '') flattenedConfig.grafana_dashboard_name = config.grafana.dashboard_name;
    if (config.grafana?.variables?.namespace !== undefined && config.grafana.variables.namespace !== '') flattenedConfig.grafana_namespace = config.grafana.variables.namespace;
    if (config.grafana?.prometheus_datasource_uid !== undefined && config.grafana.prometheus_datasource_uid !== '') flattenedConfig.grafana_prometheus_datasource_uid = config.grafana.prometheus_datasource_uid;
    if (config.grafana?.loki_datasource_uid !== undefined && config.grafana.loki_datasource_uid !== '') flattenedConfig.grafana_loki_datasource_uid = config.grafana.loki_datasource_uid;

    return this.fetchWithAuth(`/v1/configs/services/${serviceId}`, token, {
      method: 'PATCH',
      body: JSON.stringify(flattenedConfig),
    });
  }

  async deleteService(token: string, serviceId: string): Promise<void> {
    const url = `${API_BASE_URL}/v1/configs/services/${serviceId}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `API Error: ${response.status}`);
    }

    // DELETE returns 204 No Content, so no need to parse JSON
    return;
  }

  async createTeam(token: string, teamName: string, slackChannel?: string): Promise<any> {
    const payload: any = { team_name: teamName };
    if (slackChannel) {
      payload.slack_channel = slackChannel;
    }
    return this.fetchWithAuth('/v1/configs/teams', token, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateTeam(token: string, teamId: string, teamName?: string, slackChannel?: string): Promise<any> {
    const payload: any = {};
    if (teamName !== undefined) payload.team_name = teamName;
    if (slackChannel !== undefined) payload.slack_channel = slackChannel;
    return this.fetchWithAuth(`/v1/configs/teams/${teamId}`, token, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deleteTeam(token: string, teamId: string): Promise<void> {
    const url = `${API_BASE_URL}/v1/configs/teams/${teamId}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `API Error: ${response.status}`);
    }

    return;
  }

  async getUsers(token: string): Promise<any> {
    return this.fetchWithAuth('/v1/users', token);
  }

  async getIncidentMetrics(token: string): Promise<IncidentMetrics> {
    return this.fetchWithAuth('/v1/incident-metrics', token);
  }
}

export const api = new ApiService();
